<template>
    <transition name="slideUp">
        <div 
            class="Tooltip" 
            id="tooltip" 
            v-show="isTooltipOpen" 
            @click="openInfoPanel()" 
            :style="getStyle"
        >
            <img class="tooltip-logo" :src="productLogo" alt="Product Logo">
            <h2 class="tooltip-name">{{ productTitle }}</h2>
        </div>
    </transition>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useStore } from '@/store';
import { storeToRefs } from 'pinia';

const store = useStore();
const { tooltipData, activeLanguage, isTooltipOpen, products } = storeToRefs(store);

const targetProduct = computed<Product | undefined>(() => {
    if (!tooltipData.value || !products.value) 
        return undefined;
    return products.value.find((p) => p.DO_NOT_CHANGE_ID == tooltipData.value?.productID);
});

const productInfo = computed<ProductData | undefined>(() => {
    const product = targetProduct.value;
    if (!product) 
        return undefined;

    const info = product.data.find((d) => d.lang === activeLanguage.value);
    
    if (!info) {
        return product.data.find((d) => d.lang === 'en');
    }

    return info;
});

const productTitle = computed<string>(() => {
    return productInfo.value ? productInfo.value.title : '';
});

const productLogo = computed<string>(() => {
    return productInfo.value ? productInfo.value.logo : '';
});

const getStyle = computed(() => {
    if (tooltipData.value) {
        return { 
            top: tooltipData.value.top, 
            left: tooltipData.value.left 
        };
    }
    return {};
});

const openInfoPanel = () => {
    if (targetProduct.value) {
        store.setSelectedProduct(targetProduct.value);
        store.setInfoPanelOpen(true);
        store.setTooltipOpen(false);
    }
};
</script>

<style scoped>
.Tooltip {
    position: absolute;  
    cursor: pointer;
    max-width: 320px;
    min-width: 180px;
    border-radius: 2px;
    background-color: white;
    padding: 20px;
    padding-right: 40px;
    padding-bottom: 10px;
    box-shadow: 0 4px 6px rgba(0,0,0,0.1); 
}

.tooltip-name {
    position: relative;
    font-size: 1rem;
    user-select: none;
    margin: 0;
}

.tooltip-name::after {
    content: "";
    position: absolute;
    height: 12px;
    width: 12px;
    top: 50%;
    transform: translateY(-50%);
    right: -24px;
    background-image: url("/src/assets/icons/tooltip_ic_openInfo.png");
    background-repeat: no-repeat;
    background-size: contain;
    z-index: 2;
}

.tooltip-logo {
    width: 60px;
    display: block;
    margin-bottom: 10px;
}

.slideUp-enter-active, .slideUp-leave-active {
    transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
}

.slideUp-enter-from, .slideUp-leave-to {
    transform: translateY(28px);
    opacity: 0;
}
</style>